import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO } from "@shaarp/common";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { RiMailLine, RiPhoneLine } from "react-icons/ri";
import { Button } from "../ui/button";

/**
 * Normalizes a phone number by stripping all non-digit characters
 * except leading '+'. Used as fallback when normalizedPhone is not available.
 */
const normalizePhoneString = (phone: string): string =>
  phone.replace(/(?!^\+)\D/g, "");

/**
 * Checks if a phone number has a valid format (digits, spaces, dashes, dots, parens, plus).
 */
const isValidPhone = (phone: string): boolean =>
  /^\+?[\d\s\-().]{6,}$/.test(phone);

/**
 * Checks if an email has a basic valid format.
 */
const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Deduplicates phones by normalizedPhone (E.164) if available,
 * falling back to stripped-digit normalization.
 */
const deduplicatePhones = (
  phones: { phone: string; normalizedPhone?: string; source: string | null }[]
): { phone: string; normalizedPhone?: string; source: string | null }[] => {
  const seen = new Set<string>();
  return phones.filter((p) => {
    const key = p.normalizedPhone || normalizePhoneString(p.phone);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Deduplicates emails by lowercase form.
 */
const deduplicateEmails = (
  emails: { email: string; source: string | null }[]
): { email: string; source: string | null }[] => {
  const seen = new Set<string>();
  return emails.filter((e) => {
    const normalized = e.email.toLowerCase().trim();
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

/**
 * Small copy button that shows a check icon for 2 seconds after copying.
 */
function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <Button
      size="icon"
      variant="ghost"
      className="size-6 shrink-0"
      onClick={(e) => {
        e.stopPropagation();
        handleCopy();
      }}
    >
      {copied ? (
        <CheckIcon className="size-3 text-green-500" />
      ) : (
        <CopyIcon className="size-3 text-muted-foreground" />
      )}
    </Button>
  );
}

/**
 * Single contact item row with clickable link and copy button.
 */
function ContactItem({
  value,
  href,
}: {
  value: string;
  href: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <a
        href={href}
        className="text-sm hover:underline truncate"
      >
        {value}
      </a>
      <CopyButton value={value} />
    </div>
  );
}

export default function DataTableCellCompanyContact({
  company,
}: {
  company: CompanyDTO;
}) {
  const { t } = useTranslation();

  const phones = useMemo(
    () =>
      deduplicatePhones(
        (company.contact.phones ?? []).filter(
          (p) => p.phone && isValidPhone(p.phone)
        )
      ),
    [company.contact.phones]
  );

  const emails = useMemo(
    () =>
      deduplicateEmails(
        (company.contact.emails ?? []).filter(
          (e) => e.email && isValidEmail(e.email)
        )
      ),
    [company.contact.emails]
  );

  if (phones.length === 0 && emails.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-row justify-start gap-1">
      {phones.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <Popover>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button size="icon" variant="secondary" className="relative">
                    <RiPhoneLine />
                    {phones.length > 1 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center size-4 rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
                        {phones.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("components.dataTableCompanies.contact.phonesTooltip", {
                  count: phones.length,
                })}
              </TooltipContent>
              <PopoverContent side="top" className="w-auto min-w-48 p-3">
                <p className="text-xs text-muted-foreground mb-2">
                  {t("components.dataTableCompanies.contact.publicInfoNotice")}
                </p>
                <div className="space-y-1">
                  {phones.map((p, i) => (
                    <ContactItem
                      key={i}
                      value={p.phone}
                      href={`tel:${p.normalizedPhone || p.phone}`}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </Tooltip>
        </TooltipProvider>
      )}

      {emails.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <Popover>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button size="icon" variant="secondary" className="relative">
                    <RiMailLine />
                    {emails.length > 1 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center size-4 rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
                        {emails.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("components.dataTableCompanies.contact.emailsTooltip", {
                  count: emails.length,
                })}
              </TooltipContent>
              <PopoverContent side="top" className="w-auto min-w-48 p-3">
                <p className="text-xs text-muted-foreground mb-2">
                  {t("components.dataTableCompanies.contact.publicInfoNotice")}
                </p>
                <div className="space-y-1">
                  {emails.map((e, i) => (
                    <ContactItem
                      key={i}
                      value={e.email}
                      href={`mailto:${e.email}`}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
